import { Box } from '@chakra-ui/react'
import { ItList, ItListItem } from 'types/graphql'

import { ItListItemAnalyticsProps } from 'src/lib/hooks/analytics/useAnalytics'
import { BrandColor } from 'src/types'

import { TrackingData } from '../..'

import ContentCard from './ItemTypes/ContentCard/ContentCard'
import DocumentCard from './ItemTypes/DocumentCard/DocumentCard'
import ImageCard from './ItemTypes/ImageCard/ImageCard'
import ProductCard from './ItemTypes/ProductCard/ProductCard'
import TextCard from './ItemTypes/TextCard/TextCard'
import VoiceTipCard from './ItemTypes/VoiceTipCard/VoiceTipCard'

type ItemCardProps = {
  itList: ItList
  item: ItListItem
  brandColor: BrandColor
  index: number
  trackingData: TrackingData
  isPreview: boolean
}

const ItemCardInner: React.FC<ItemCardProps> = ({
  itList,
  brandColor,
  trackingData,
  item,
  index,
  isPreview,
}) => {
  const itListItemTrackingData: ItListItemAnalyticsProps = {
    ...trackingData,
    itemId: `${item.id}`,
    itemUrl: item.url,
  }
  const {
    url,
    type,
    document,
    product,
    content,
    text,
    voiceTipUrl,
    hasVoiceTip,
    title,
    id,
    image,
  } = item
  switch (type) {
    case 'product':
      return (
        <ProductCard
          profilePictureUrl={itList.creator.profileImage}
          brandColor={brandColor}
          url={url}
          voiceTipUrl={voiceTipUrl}
          hasVoiceTip={hasVoiceTip}
          title={title}
          trackingData={itListItemTrackingData}
          data-testid="itemCard-product"
          productItem={product}
          itemId={id}
        />
      )
    case 'document':
      return (
        <DocumentCard
          profilePictureUrl={itList.creator.profileImage}
          brandColor={brandColor}
          voiceTipUrl={voiceTipUrl}
          hasVoiceTip={hasVoiceTip}
          title={title}
          trackingData={itListItemTrackingData}
          data-testid="itemCard-document"
          documentItem={document}
          itemId={id}
        />
      )
    case 'content':
      return (
        <ContentCard
          profilePictureUrl={itList.creator.profileImage}
          brandColor={brandColor}
          url={url}
          voiceTipUrl={voiceTipUrl}
          hasVoiceTip={hasVoiceTip}
          title={title}
          trackingData={itListItemTrackingData}
          data-testid="itemCard-content"
          contentItem={content}
          itemId={id}
        />
      )
    case 'text':
      return (
        <TextCard
          isFirstItem={index === 0}
          brandColor={brandColor}
          heading={text?.heading}
          data-testid="itemCard-text"
          description={text?.description}
          itemId={id}
        />
      )
    case 'voiceTip':
      return (
        <VoiceTipCard
          brandColor={brandColor}
          label={title}
          voiceTipUrl={voiceTipUrl}
          hasVoiceTip={hasVoiceTip}
          isPreview={isPreview}
          data-testid="itemCard-voiceTip"
          trackingData={itListItemTrackingData}
          itemId={id}
        />
      )
    case 'image':
      return (
        <ImageCard
          brandColor={brandColor}
          imageUrl={image?.coverUrl}
          description={image?.description}
          title={title}
          isPreview={isPreview}
          voiceTipUrl={voiceTipUrl}
          hasVoiceTip={hasVoiceTip}
          data-testid="itemCard-image"
          profilePictureUrl={itList.creator.profileImage}
          trackingData={itListItemTrackingData}
          itemId={id}
        />
      )

    default:
      console.error(`Couldn't find type: ${type}`)
      return null
  }
}

const ItemCard: React.FC<ItemCardProps> = (props) => (
  <Box
    px={{ base: 4, lg: props.isPreview ? 4 : 24 }}
    mt={4}
    data-testid="itemCard-wrapper"
    id={`it-list-item-${props.item.id}`}
  >
    <ItemCardInner {...props} />
  </Box>
)

export default React.memo(ItemCard)
